import { z } from "zod";
import { AgencyIdSchema, ProjectIdSchema, ReportIdSchema, RevisionIdSchema, RunIdSchema, } from "../primitives/ids.js";
import { ArtifactTruthV1Schema } from "./artifact-truth.js";
import { TIER1_DK_SCHEMA_VERSION } from "./tier1-departure-kit-paths.js";
export const DepartureKitAvailabilitySchema = z.enum(["AVAILABLE", "UNAVAILABLE"]);
export const PassingVsApprovedDisclosureV1Schema = z
    .object({
    schemaVersion: z.literal("passing-vs-approved-disclosure.v1.0"),
    latestPassingArtifactSetHash: z.string().min(1),
    latestApprovedArtifactSetHash: z.string().min(1),
    latestPassingRecordedAt: z.string().datetime(),
    latestApprovedRecordedAt: z.string().datetime(),
    clientSafeSummary: z.string().min(1),
    internalNote: z.string().min(1),
})
    .strict();
export const DepartureKitManifestFileEntryV1Schema = z
    .object({
    relativePath: z.string().min(1),
    sha256Hex: z.string().min(1),
    availability: DepartureKitAvailabilitySchema.default("AVAILABLE"),
})
    .strict();
export const DepartureKitManifestV1Schema = z
    .object({
    schemaVersion: z.literal("departure-kit-manifest.v1.0"),
    dkSchemaVersion: z.enum([TIER1_DK_SCHEMA_VERSION, "tier0-minimal-dk.v1.0"]),
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    runId: RunIdSchema,
    businessGraphRevisionId: RevisionIdSchema,
    artifactSetHash: z.string().min(1),
    exportBundleHash: z.string().min(1),
    departureKitManifestHash: z.string().min(1),
    generatedAt: z.string().datetime(),
    fileEntries: z.array(DepartureKitManifestFileEntryV1Schema).min(1),
    searchReadinessAvailability: DepartureKitAvailabilitySchema,
    launchProofAvailability: DepartureKitAvailabilitySchema.optional().default("UNAVAILABLE"),
    connectedAccountsAvailability: DepartureKitAvailabilitySchema,
    reportId: ReportIdSchema.optional(),
    passingVsApproved: PassingVsApprovedDisclosureV1Schema.nullable(),
})
    .strict();
export const DepartureKitIssuanceStatusV1Schema = z.enum([
    "BLOCKED",
    /** QA passed — handoff can be requested; export not started. */
    "ELIGIBLE",
    /** @deprecated Use ELIGIBLE or EXPORT_QUEUED — kept for persisted records only. */
    "REQUESTED",
    /** Export requested (EXPORT_READY) — worker not started yet. */
    "EXPORT_QUEUED",
    "EXPORTING",
    "COMPOSING",
    "READY",
    "FAILED",
]);
export const DepartureKitExportRecordV1Schema = z
    .object({
    schemaVersion: z.literal("departure-kit-export-record.v1.0"),
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    runId: RunIdSchema,
    exportBundleHash: z.string().min(1),
    departureKitManifestHash: z.string().min(1),
    status: DepartureKitIssuanceStatusV1Schema,
    dkSchemaVersion: z.literal(TIER1_DK_SCHEMA_VERSION),
    traceId: z.string().min(1),
    correlationId: z.string().min(1).optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const HandoffGuideSectionV1Schema = z
    .object({
    id: z.string().min(1),
    title: z.string().min(1),
    bodyMarkdown: z.string(),
    clientSafe: z.boolean(),
})
    .strict();
export const HandoffGuideV1Schema = z
    .object({
    schemaVersion: z.literal("handoff-guide.v1.0"),
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    sections: z.array(HandoffGuideSectionV1Schema).min(1),
    generatedAt: z.string().datetime(),
})
    .strict();
export const ArtifactTruthBundleV1Schema = z
    .object({
    schemaVersion: z.literal("artifact-truth-bundle.v1.0"),
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    runId: RunIdSchema,
    bundleHash: z.string().min(1),
    artifacts: z.array(ArtifactTruthV1Schema).min(1),
    generatedAt: z.string().datetime(),
})
    .strict();
export const ClientSafeHandoffChecklistItemV1Schema = z
    .object({
    label: z.string().min(1),
    included: z.boolean(),
})
    .strict();
export const ClientSafeHandoffViewV1Schema = z
    .object({
    schemaVersion: z.literal("client-safe-handoff-view.v1.0"),
    headline: z.string().min(1),
    subcopy: z.string().min(1),
    issuanceStatus: DepartureKitIssuanceStatusV1Schema,
    checklist: z.array(ClientSafeHandoffChecklistItemV1Schema).min(1),
    mismatchBanner: z.string().nullable(),
    downloadAvailable: z.boolean(),
    whatWeDoNotPromise: z.array(z.string().min(1)).min(1),
    generatedAt: z.string().datetime(),
})
    .strict();
export const StudioDepartureKitStatusV1Schema = z
    .object({
    schemaVersion: z.literal("studio-departure-kit-status.v1.0"),
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    runId: RunIdSchema.optional(),
    issuanceStatus: DepartureKitIssuanceStatusV1Schema,
    exportBundleHash: z.string().min(1).optional(),
    departureKitManifestHash: z.string().min(1).optional(),
    artifactSetHash: z.string().min(1).optional(),
    passingVsApproved: PassingVsApprovedDisclosureV1Schema.nullable(),
    downloadAvailable: z.boolean(),
})
    .strict();
//# sourceMappingURL=departure-kit-v1.js.map